import {Clock} from 'lucide-react'
import React, {FC, useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

/**
 * Compact time picker for the onboarding wizard.
 *
 * Replaces the native `<input type='time'>` whose picker is drawn by the OS
 * (the full-width wheel sheet on iOS, the clock dialog on Android) and can be
 * neither styled nor resized. This one keeps the familiar wheel feel - hour,
 * minute and AM/PM columns that scroll and snap, the centre row being the
 * selection - but as a small popover under the field, on the WizardDatePicker
 * surface (`.wizard-datepicker-trigger` / `-popover`, see `_onboardingwizard.scss`)
 * so the wizard's date and time fields read as one family.
 *
 * Value is a 24-hour `HH:mm` string (or `''`), exactly what the native input
 * produced, so callers' `timeToMinutes` comparisons and the saved timings keep
 * their format. Every wheel settle or tap commits immediately; Done, Escape, an
 * outside tap or an ancestor scroll close the popover.
 *
 * Portalled to `document.body` and positioned `fixed` from the trigger's rect
 * for the same reason as the date picker: the edit sheet scrolls and sits in a
 * transformed container, which would clip or re-base an in-place popover.
 */

interface Props {
  /** 24-hour `HH:mm`, or `''` for no selection. */
  value: string
  onChange: (hhmm: string) => void
  /** Element id for the trigger so a `<label htmlFor>` can target it. */
  id?: string
  /** Extra classes on the trigger (e.g. the sheet's `step2-edit-sheet-time-input`). */
  className?: string
  /** Inline trigger styles so a host can match its neighbouring inputs. */
  style?: React.CSSProperties
  /** Already-translated placeholder shown when there is no value. */
  placeholder?: string
  ariaLabel?: string
  disabled?: boolean
  /** Minute granularity of the minute wheel; an off-grid current value is still listed. */
  minuteStep?: number
  /**
   * How the trigger shows its value: the locale's 12-hour form ("8:00 AM") or the
   * raw `HH:mm` the native input displayed ("08:00"). Default `'12h'`.
   */
  format?: '12h' | '24h'
  /** Show the clock icon at the end of the trigger. Default `true`. */
  icon?: boolean
}

type Hm = {h: number; m: number}
type Period = 'AM' | 'PM'

const POPOVER_MIN_WIDTH = 220
const POPOVER_MAX_WIDTH = 300
/** Wheel area + footer + padding; only used to decide drop-up vs drop-down. */
const POPOVER_HEIGHT = 250
const VIEWPORT_MARGIN = 8
const HOURS_12 = Array.from({length: 12}, (_, i) => i + 1)
/** Must match `.wizard-timepicker-wheel-item` height and the wheel padding in scss. */
const ITEM_HEIGHT = 36

const pad = (n: number) => String(n).padStart(2, '0')
const toHhmm = ({h, m}: Hm) => `${pad(h)}:${pad(m)}`
const parseHhmm = (value: string | undefined): Hm | null => {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  if (h < 0 || h > 23 || m < 0 || m > 59) return null
  return {h, m}
}
const periodOf = (h: number): Period => (h < 12 ? 'AM' : 'PM')
const hour12Of = (h: number) => h % 12 || 12
const hour24Of = (h12: number, period: Period) => (h12 % 12) + (period === 'PM' ? 12 : 0)
/** Date carrying just the wall-clock time, for intl formatting. */
const toDate = ({h, m}: Hm) => new Date(2000, 0, 1, h, m)
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))

/* --------------------------------- wheel ---------------------------------- */

interface WheelProps {
  items: {key: string; label: string}[]
  selectedIndex: number
  onSelect: (index: number) => void
  ariaLabel?: string
}

/**
 * One snap-scrolling column. The padding rows above and below let the first
 * and last items reach the centre band, so `scrollTop = index * ITEM_HEIGHT`
 * centres item `index`. A scroll commits once it has settled (no scroll event
 * for a beat); tapping an item commits at once and scrolls it into the band.
 */
const Wheel: FC<WheelProps> = ({items, selectedIndex, onSelect, ariaLabel}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const settleTimer = useRef<number | null>(null)

  // Bring the wheel to the selection when it changes from outside (open, tap,
  // a new value). After a user scroll settles, the selection already equals
  // the resting position, so this does not fight the finger.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const target = selectedIndex * ITEM_HEIGHT
    if (Math.abs(el.scrollTop - target) > 1) el.scrollTop = target
  }, [selectedIndex])

  useEffect(
    () => () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current)
    },
    []
  )

  const settle = () => {
    const el = ref.current
    if (!el) return
    const index = clamp(Math.round(el.scrollTop / ITEM_HEIGHT), 0, items.length - 1)
    if (index !== selectedIndex) onSelect(index)
  }
  const onScroll = () => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current)
    settleTimer.current = window.setTimeout(settle, 120)
  }

  return (
    <div
      ref={ref}
      className='wizard-timepicker-wheel'
      role='listbox'
      aria-label={ariaLabel}
      onScroll={onScroll}
    >
      <div className='wizard-timepicker-wheel-pad' aria-hidden />
      {items.map((item, i) => {
        const isSelected = i === selectedIndex
        return (
          <button
            key={item.key}
            type='button'
            role='option'
            aria-selected={isSelected}
            className={`wizard-timepicker-wheel-item${isSelected ? ' is-selected' : ''}`}
            onClick={() => {
              onSelect(i)
              ref.current?.scrollTo({top: i * ITEM_HEIGHT, behavior: 'smooth'})
            }}
          >
            {item.label}
          </button>
        )
      })}
      <div className='wizard-timepicker-wheel-pad' aria-hidden />
    </div>
  )
}

/* -------------------------------- picker ---------------------------------- */

const WizardTimePicker: FC<Props> = ({
  value,
  onChange,
  id,
  className = '',
  style,
  placeholder,
  ariaLabel,
  disabled = false,
  minuteStep = 1,
  format = '12h',
  icon = true,
}) => {
  const intl = useIntl()
  const fallbackId = useId()
  const triggerId = id ?? fallbackId
  const popoverId = `${triggerId}-time`

  const selected = parseHhmm(value)

  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<React.CSSProperties | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const measure = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    const spaceBelow = vh - r.bottom - 4 - VIEWPORT_MARGIN
    const spaceAbove = r.top - 4 - VIEWPORT_MARGIN
    const dropUp = spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow
    const width = Math.min(
      Math.max(r.width, POPOVER_MIN_WIDTH),
      POPOVER_MAX_WIDTH,
      vw - VIEWPORT_MARGIN * 2
    )
    // Grow away from the trigger's neighbour: the End field (right half of the
    // row) opens leftwards, and RTL documents anchor on the right edge.
    const rtl = document.documentElement.dir === 'rtl'
    const anchorRight = rtl || r.left + r.width / 2 > vw / 2
    const rawLeft = anchorRight ? r.right - width : r.left
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rawLeft, vw - width - VIEWPORT_MARGIN))
    setPos({
      ...(dropUp ? {bottom: vh - r.top + 4} : {top: r.bottom + 4}),
      left,
      width,
    })
  }, [])

  useLayoutEffect(() => {
    if (open) measure()
  }, [open, measure])

  useEffect(() => {
    if (!open) return
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // Capture phase so any scrolling ancestor closes the popover (its position
    // was measured once and would otherwise drift). The wheels scroll inside
    // the popover and must not dismiss it.
    const onScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const close = () => setOpen(false)
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('touchstart', onDocPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('touchstart', onDocPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  /* ------------------------------- picking -------------------------------- */

  // With no value yet the wheels rest on 8:00 AM, and the first interaction
  // fills the other parts from it so the field never holds a half-formed time.
  const base: Hm = selected ?? {h: 8, m: 0}
  const currentHour12 = hour12Of(base.h)
  const currentPeriod = periodOf(base.h)

  const step = clamp(Math.floor(minuteStep), 1, 30)
  const minutes = Array.from({length: Math.ceil(60 / step)}, (_, i) => i * step)
  if (!minutes.includes(base.m)) {
    minutes.push(base.m)
    minutes.sort((a, b) => a - b)
  }

  const pickHour = (h12: number) => onChange(toHhmm({h: hour24Of(h12, currentPeriod), m: base.m}))
  const pickMinute = (m: number) => onChange(toHhmm({h: base.h, m}))
  const pickPeriod = (period: Period) =>
    onChange(toHhmm({h: hour24Of(currentHour12, period), m: base.m}))

  const done = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  /* ------------------------------- labels --------------------------------- */

  const periodLabel = (period: Period) =>
    intl
      .formatTimeToParts(toDate({h: period === 'AM' ? 9 : 21, m: 0}), {
        hour: 'numeric',
        hour12: true,
      })
      .find((p) => p.type === 'dayPeriod')?.value ?? period
  const periods: Period[] = ['AM', 'PM']

  const displayValue = !selected
    ? ''
    : format === '24h'
      ? toHhmm(selected)
      : intl.formatTime(toDate(selected), {hour: 'numeric', minute: '2-digit', hour12: true})

  const popover = open && pos && (
    <div
      id={popoverId}
      ref={popoverRef}
      role='dialog'
      aria-modal='false'
      aria-label={ariaLabel}
      className='wizard-datepicker-popover wizard-timepicker-popover'
      style={pos}
    >
      <div className='wizard-timepicker-wheels'>
        <Wheel
          ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.TIMEPICKER.HOUR'})}
          items={HOURS_12.map((h12) => ({key: String(h12), label: String(h12)}))}
          selectedIndex={HOURS_12.indexOf(currentHour12)}
          onSelect={(i) => pickHour(HOURS_12[i])}
        />
        <Wheel
          ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.TIMEPICKER.MINUTE'})}
          items={minutes.map((m) => ({key: String(m), label: pad(m)}))}
          selectedIndex={minutes.indexOf(base.m)}
          onSelect={(i) => pickMinute(minutes[i])}
        />
        <Wheel
          items={periods.map((p) => ({key: p, label: periodLabel(p)}))}
          selectedIndex={periods.indexOf(currentPeriod)}
          onSelect={(i) => pickPeriod(periods[i])}
        />
        {/* Centre band marking the selected row, drawn over all three wheels. */}
        <div className='wizard-timepicker-wheel-band' aria-hidden />
      </div>

      <div className='wizard-datepicker-footer wizard-timepicker-footer'>
        <button type='button' className='wizard-datepicker-action' onClick={done}>
          {intl.formatMessage({id: 'GENERAL.DONE'})}
        </button>
      </div>
    </div>
  )

  return (
    <>
      <button
        id={triggerId}
        ref={triggerRef}
        type='button'
        disabled={disabled}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={ariaLabel}
        className={`wizard-datepicker-trigger${open ? ' is-open' : ''}${displayValue ? '' : ' is-empty'} ${className}`.trim()}
        onClick={() => setOpen((o) => !o)}
        style={style}
      >
        <span className='wizard-datepicker-value'>
          {displayValue ||
            placeholder ||
            intl.formatMessage({id: 'ONBOARDING_WIZARD.TIMEPICKER.PLACEHOLDER'})}
        </span>
        {icon && <Clock size={16} className='wizard-datepicker-icon' aria-hidden />}
      </button>
      {popover && createPortal(popover, document.body)}
    </>
  )
}

export default WizardTimePicker
